import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { SelectInput } from "@/components/ui/Field";
import { ApiError } from "@/lib/apiClient";
import { useCurrentUser, userHasGroup, userIsBuero } from "@/lib/auth";
import {
  listOrdner,
  listYears,
  moveProjectOrdner,
  previewOrdnerUmzug,
  type Order,
  type OrdnerOut,
  type OrdnerUmzugPreview,
} from "@/lib/projects";
import { toast } from "@/lib/toast";
import { projectFolderYearMismatch } from "@/projectOverview/ordnerJahr";

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function OrdnerJahrBadge({
  project,
  onMoved,
}: {
  project: Order;
  onMoved?: () => void;
}) {
  const { user } = useCurrentUser();
  const [open, setOpen] = useState(false);
  const mismatch = projectFolderYearMismatch(project);
  const canMove = userIsBuero(user) || userHasGroup(user, "App-Admins");
  const year = project.ordner_jahr;

  if (project.sordner_id && !project.ordner_id) return null;
  if (year == null) return null;

  const label = `Ordner ${year}`;
  const title = mismatch
    ? `Projektjahr ${project.jahr}, Ordnerjahr ${year}. Zum Verschieben klicken.`
    : `Liegt im Ordnerjahr ${year}`;

  if (!mismatch || !canMove) {
    return (
      <span
        className={clsx("eh-ordner-jahr-badge", mismatch && "eh-ordner-jahr-badge--mismatch")}
        title={title}
      >
        {label}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        className="eh-ordner-jahr-badge eh-ordner-jahr-badge--mismatch"
        title={title}
        onClick={(event) => {
          event.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {label}
      </button>
      {open ? (
        <OrdnerUmzugDialog
          project={project}
          onClose={() => setOpen(false)}
          onMoved={onMoved}
        />
      ) : null}
    </>
  );
}

function OrdnerUmzugDialog({
  project,
  onClose,
  onMoved,
}: {
  project: Order;
  onClose: () => void;
  onMoved?: () => void;
}) {
  const [years, setYears] = useState<number[]>([]);
  const [year, setYear] = useState<number | null>(project.jahr ?? project.ordner_jahr ?? null);
  const [folders, setFolders] = useState<OrdnerOut[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [ordnerId, setOrdnerId] = useState<number | null>(null);
  const [preview, setPreview] = useState<OrdnerUmzugPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void listYears()
      .then((list) => {
        if (cancelled) return;
        const extra = [project.jahr, project.ordner_jahr].filter(
          (value): value is number => value != null,
        );
        const merged = Array.from(new Set([...list, ...extra])).sort((a, b) => b - a);
        setYears(merged);
      })
      .catch(() => {
        if (!cancelled) setYears([]);
      });
    return () => {
      cancelled = true;
    };
  }, [project.jahr, project.ordner_jahr]);

  useEffect(() => {
    if (year == null) return;
    let cancelled = false;
    setFoldersLoading(true);
    setOrdnerId(null);
    setPreview(null);
    void listOrdner(year)
      .then((list) => {
        if (cancelled) return;
        const sorted = [...list].sort((a, b) =>
          a.nummer.localeCompare(b.nummer, "de", { numeric: true }),
        );
        setFolders(sorted);
        const sameNumber = sorted.find((folder) => folder.nummer === project.ordnernummer);
        setOrdnerId(sameNumber?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      })
      .finally(() => {
        if (!cancelled) setFoldersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year, project.ordnernummer]);

  useEffect(() => {
    if (ordnerId == null) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    void previewOrdnerUmzug(project.id, ordnerId)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setPreviewError(errorMessage(err, "Vorschau konnte nicht geladen werden."));
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ordnerId, project.id]);

  const folderLabel = useMemo(() => {
    if (!preview) return "";
    const desc = preview.ordner_beschreibung.trim();
    return desc ? `${preview.ordner_nummer} – ${desc}` : preview.ordner_nummer;
  }, [preview]);

  async function handleMove() {
    if (ordnerId == null || !preview || preview.bereits_dort) return;
    setBusy(true);
    try {
      const result = await moveProjectOrdner(project.id, ordnerId);
      const numberNote =
        result.alte_auftragsnummer && result.alte_auftragsnummer !== result.auftragsnummer
          ? ` Neue Nummer: ${result.auftragsnummer}.`
          : "";
      toast.success(
        `Projekt liegt jetzt im Ordner ${result.ordner_nummer} (${result.ordner_jahr}).${numberNote}`,
      );
      onClose();
      onMoved?.();
    } catch (err) {
      toast.error(errorMessage(err, "Verschieben fehlgeschlagen."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title="Ordnerjahr ändern"
      description={`Projekt ${project.auftragsnummer || ""} liegt im Ordnerjahr ${project.ordner_jahr ?? "—"}, das Projektjahr ist ${project.jahr ?? "—"}.`}
      size="md"
      footer={
        <>
          <Button type="button" variant="secondary" disabled={busy} onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            type="button"
            loading={busy}
            disabled={!preview || preview.bereits_dort || previewLoading || ordnerId == null}
            onClick={() => void handleMove()}
          >
            In diesen Ordner verschieben
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-label-secondary">
        <SelectInput
          id={`ordner-jahr-${project.id}`}
          label="Ordnerjahr"
          value={year ?? ""}
          onChange={(event) => setYear(Number(event.target.value))}
        >
          {years.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </SelectInput>
        <SelectInput
          id={`ordner-ziel-${project.id}`}
          label="Ordner"
          value={ordnerId ?? ""}
          disabled={foldersLoading || year == null}
          onChange={(event) => setOrdnerId(Number(event.target.value) || null)}
        >
          <option value="">{foldersLoading ? "Ordner werden geladen …" : "Ordner wählen"}</option>
          {folders.map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.nummer}
              {folder.beschreibung ? ` – ${folder.beschreibung}` : ""}
            </option>
          ))}
        </SelectInput>
        {previewError ? <p className="text-[var(--eh-red)]">{previewError}</p> : null}
        {previewLoading ? <p>Prüfe die Nummer im Zielordner …</p> : null}
        {preview?.bereits_dort ? <p>Das Projekt liegt schon in diesem Ordner.</p> : null}
        {preview && !preview.bereits_dort ? (
          <div className="space-y-2">
            <p>
              Das Projekt kommt in den Ordner {folderLabel} ({preview.ordner_jahr}). Das Projektjahr
              wird auf {preview.ordner_jahr} gesetzt, damit Filter und Ordner zusammenpassen.
            </p>
            {preview.nummer_belegt ? (
              <p>
                Die Nummer {project.auftragsnummer} ist dort schon vergeben. Das Projekt erhält{" "}
                {preview.auftragsnummer}.
              </p>
            ) : (
              <p>Die Nummer {preview.auftragsnummer} bleibt erhalten.</p>
            )}
            <p>Der SharePoint-Ordner zieht mit, sofern er vorhanden ist.</p>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}
